import ChevronDownRoundedIcon from '@assets/icons/chevron-down-rounded.svg';
import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { AlertCard, Props as AlertCardProps } from './alert-card';
import { AlertFeedEmpty } from './alert-feed-empty';
import { Analytics } from '@/utils/analytics';
import { createStyles } from '@/contexts/theme-context';
import {
  FlatList,
  LayoutAnimation,
  Platform,
  UIManager,
  View,
} from 'react-native';
import {
  HubCardData,
  SalesAlertsHubCardData,
} from '@/hooks/insights-hub/types';
import { HubCardType } from '@/types';
import { HubFeedCardsList } from './hub-feed-cards-list';
import { Indicator } from './indicator';
import { ListItemSeparator } from '@/components/list-item';
import { shadow } from '@/styles';
import { useLayoutContext } from '@/contexts/layout-context';

export interface Props {
  data: SalesAlertsHubCardData[];
  onPress: (cardData: HubCardData) => void;
  onPressSupportInfo: (cardData: HubCardData) => void;
}

// Required for LayoutAnimation to work on Android
// https://reactnative.dev/docs/layoutanimation.html
if (Platform.OS === 'android') {
  if (UIManager.setLayoutAnimationEnabledExperimental) {
    UIManager.setLayoutAnimationEnabledExperimental(true);
  }
}

export const AlertFeed: React.FC<Props> = ({
  data,
  onPress,
  onPressSupportInfo,
}) => {
  const { isSmallLayout } = useLayoutContext();
  const [styles] = useStyles();

  const [expandedGroups, setExpandedGroups] = useState<
    Partial<Record<HubCardType, boolean>>
  >({
    [HubCardType.OverduePayment]: false,
    [HubCardType.LostItem]: false,
    [HubCardType.ChurnRisk]: false,
  });

  const toggleGroup = (alertCount: number, cardType: HubCardType) => {
    LayoutAnimation.configureNext({
      duration: 400,
      update: {
        ...LayoutAnimation.Presets.spring.update,
        springDamping: 0.9,
        type: LayoutAnimation.Types.spring,
      },
    });
    setExpandedGroups((prev) => ({
      ...prev,
      [cardType]: !prev[cardType],
    }));
    Analytics.track('insights_hub.alert.indicator.press', {
      alertCount,
      cardType,
    });
  };

  // render each alert stack group
  const renderGroups = (alert: SalesAlertsHubCardData, index: number) => {
    const isExpanded = expandedGroups[alert.cardType];

    // no alerts, render nothing
    if (!alert.cardData || alert.cardData.length === 0) {
      return null;
    }

    const hasMultipleAlerts = alert.cardData.length > 1;

    const alertCardProps: AlertCardProps = {
      containerStyle: styles.cardMobile,
      index,
      item: alert.cardData[0] as HubCardData,
      onPress: () => onPress(alert.cardData[0] as HubCardData),
      onPressSupportInfo: () =>
        onPressSupportInfo(alert.cardData[0] as HubCardData),
    };

    // cardData is an array of alerts, so alertCount is the number of alerts - the top alert, which is 1 less than the length
    const alertCount = alert.cardData.length - 1;

    return (
      <>
        {/* Single alert (no expand/collapse needed) */}
        {!hasMultipleAlerts ? (
          <AlertCard {...alertCardProps}>
            {!isSmallLayout ? (
              <ListItemSeparator style={styles.separator} />
            ) : null}
          </AlertCard>
        ) : null}

        {/* Collapsed view (first alert + expand icon) */}
        {hasMultipleAlerts && !isExpanded ? (
          <View>
            <View style={isSmallLayout ? styles.cardFront : null}>
              <AlertCard
                {...alertCardProps}
                containerStyle={[
                  alertCardProps.containerStyle,
                  styles.indicatorLessPadding,
                ]}
              >
                <Indicator
                  accessibilityLabel={i18n.t('expand')}
                  count={alertCount}
                  Icon={ChevronDownRoundedIcon}
                  onPress={() => toggleGroup(alertCount, alert.cardType)}
                />
                {!isSmallLayout ? (
                  <ListItemSeparator style={styles.separator} />
                ) : null}
              </AlertCard>
            </View>
            {isSmallLayout ? (
              // we only display grouped cards on mobile
              <>
                <View style={styles.cardMiddleContainer}>
                  <View style={styles.cardSpacerSmall} />
                  <View style={[styles.cardMobile, styles.cardMiddle]} />
                  <View style={styles.cardSpacerSmall} />
                </View>
                <View style={styles.cardBackContainer}>
                  <View style={styles.cardSpacerLarge} />
                  <View style={[styles.cardMobile, styles.cardBack]} />
                  <View style={styles.cardSpacerLarge} />
                </View>
              </>
            ) : null}
          </View>
        ) : null}

        {/* Expanded view (all alerts + collapse icon) */}
        {hasMultipleAlerts && isExpanded ? (
          <HubFeedCardsList
            alerts={alert.cardData as HubCardData[]}
            ItemSeparatorComponent={() =>
              isSmallLayout ? (
                <ListItemSeparator style={styles.separator} />
              ) : null
            }
            onPress={onPress}
            onPressSupportInfo={onPressSupportInfo}
            toggleGroup={toggleGroup}
          />
        ) : null}
      </>
    );
  };

  if (data.length === 0) {
    // return empty state
    return <AlertFeedEmpty />;
  }

  return (
    <FlatList
      contentContainerStyle={isSmallLayout ? styles.container : null}
      data={data}
      keyExtractor={(alert) => alert.cardType}
      renderItem={({ item: alert, index }) => renderGroups(alert, index)}
      scrollEnabled={false}
      showsHorizontalScrollIndicator={false}
      showsVerticalScrollIndicator={false}
    />
  );
};

const useStyles = createStyles((theme) => ({
  cardBack: {
    backgroundColor: theme.sk.color.bg.card.back,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    flex: 1,
    height: 12,
    marginTop: -6,
  },
  cardBackContainer: {
    flex: 1,
    flexDirection: 'row',
    zIndex: 1,
  },
  cardFront: {
    zIndex: 3,
    ...shadow({
      opacity: 0.02,
      radius: 4,
      x: 0,
      y: 2,
    }),
  },
  cardMiddle: {
    backgroundColor: theme.sk.color.bg.card.mid,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    flex: 1,
    height: 12,
    marginTop: -6,
    ...shadow({
      opacity: 0.04,
      radius: 4,
      x: 0,
      y: 2,
    }),
  },
  cardMiddleContainer: {
    flex: 1,
    flexDirection: 'row',
    zIndex: 2,
  },
  cardMobile: {
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  cardSpacerLarge: {
    width: 12,
  },
  cardSpacerSmall: {
    width: 6,
  },
  container: {
    gap: 8,
  },
  indicatorLessPadding: {
    paddingBottom: 10,
  },
  separator: {
    borderBottomColor: theme.sk.color.divider.default,
    borderBottomWidth: 1,
    marginHorizontal: 0,
  },
}));
